import { useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PawPrint, X } from 'lucide-react'
import { listPatients } from '../../api/patients'
import { ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox } from '../../components/ui/Card'
import { useFetch } from '../../hooks/useFetch'
import { thClass, tdClass, tbodyHoverClass, skeletonClass } from '@/lib/tableStyles'
import { SPECIES_LABELS } from '../../types/patients'

const PAGE_SIZE = 25

export default function PatientList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') ?? ''
  const offset = Number(searchParams.get('offset') ?? '0')
  const [inputValue, setInputValue] = useState(search)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data, loading, error } = useFetch(
    () => listPatients({ search: search || undefined, limit: PAGE_SIZE, offset }),
    [search, offset],
  )

  const patients = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1
  const start = total === 0 ? 0 : offset + 1
  const end = Math.min(offset + PAGE_SIZE, total)

  const handleSearch = (value: string) => {
    setInputValue(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchParams(value ? { q: value } : {}, { replace: true })
    }, 300)
  }

  const handleClear = () => {
    setInputValue('')
    setSearchParams({}, { replace: true })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">Patients</h1>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-[360px]">
          <input
            className="w-full py-[0.6rem] pl-[0.85rem] pr-8 border border-border-input rounded text-[0.9rem] outline-none bg-surface text-text box-border"
            type="search"
            placeholder="Search by name, breed, microchip…"
            value={inputValue}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {inputValue && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-transparent border-none text-text-3 cursor-pointer leading-none p-0 flex items-center"
              onClick={handleClear}
            >
              <X size={14} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className={thClass}>Name</th>
                <th className={thClass}>Species</th>
                <th className={thClass}>Breed</th>
                <th className={thClass}>Owner</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={tbodyHoverClass}>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <td key={j} className={tdClass}>
                        <div className={`${skeletonClass} h-3.5 ${j === 4 ? 'w-16' : 'w-3/4'}`} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : patients.length === 0 ? (
                <tr>
                  <td colSpan={5} className={tdClass}>
                    <div className="text-center py-12 px-4 text-text-3">
                      <PawPrint size={36} strokeWidth={1.25} className="text-text-4 mb-2 mx-auto" />
                      <div className="font-semibold text-text-heading mb-[0.35rem]">No patients found</div>
                      <div className="text-sm">
                        {search ? 'Try a different search term' : 'Patients are added via client records'}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                patients.map((p) => (
                  <tr key={p.id}>
                    <td className={`${tdClass} font-semibold`}>
                      <Link to={`/patients/${p.id}`} className="text-accent no-underline hover:underline">
                        {p.name}
                      </Link>
                    </td>
                    <td className={tdClass}>{SPECIES_LABELS[p.species]}</td>
                    <td className={`${tdClass} text-text-2`}>
                      {p.breed ?? <span className="text-text-4">—</span>}
                    </td>
                    <td className={`${tdClass} text-text-2`}>
                      {p.primary_owner_id ? (
                        <Link to={`/clients/${p.primary_owner_id}`} className="text-text-2 no-underline">
                          {p.primary_owner_name ?? `Client #${p.primary_owner_id}`}
                        </Link>
                      ) : <span className="text-text-4">—</span>}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <Link to={`/patients/${p.id}`} className="text-accent font-medium text-[0.82rem] no-underline">
                        View →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-4 py-[0.85rem] border-t border-border text-[0.85rem] text-text-2">
            <span>Showing {start}–{end} of {total}</span>
            <div className="flex gap-2">
              <ButtonLink
                to={`/patients?${search ? `q=${search}&` : ''}offset=${Math.max(0, offset - PAGE_SIZE)}`}
                variant="outline"
                size="sm"
                style={{ pointerEvents: currentPage <= 1 ? 'none' : undefined, opacity: currentPage <= 1 ? 0.4 : 1 }}
              >
                ← Prev
              </ButtonLink>
              <ButtonLink
                to={`/patients?${search ? `q=${search}&` : ''}offset=${offset + PAGE_SIZE}`}
                variant="outline"
                size="sm"
                style={{ pointerEvents: currentPage >= totalPages ? 'none' : undefined, opacity: currentPage >= totalPages ? 0.4 : 1 }}
              >
                Next →
              </ButtonLink>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
